import type { Attachment } from 'svelte/attachments'

interface DeferredVideoOptions {
  media?: string
  rootMargin?: string
}

export function deferVideo({
  media,
  rootMargin = '1200px 0px',
}: DeferredVideoOptions = {}): Attachment<HTMLVideoElement> {
  return (video) => {
    let loaded = false
    let visible = false
    const reducedMotionQuery = '(prefers-reduced-motion: no-preference)'
    const mediaQuery = window.matchMedia(media ? `${media} and ${reducedMotionQuery}` : reducedMotionQuery)

    const load = () => {
      if (loaded || !mediaQuery.matches) return
      loaded = true

      if (video.dataset.src) {
        video.src = video.dataset.src
        delete video.dataset.src
      }
      for (const source of video.querySelectorAll<HTMLSourceElement>('source[data-src]')) {
        source.src = source.dataset.src ?? ''
        delete source.dataset.src
      }
      video.load()
      if (visible && video.autoplay) void video.play().catch(() => {})
    }

    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            ([entry]) => {
              visible = entry?.isIntersecting ?? false
              if (visible) {
                load()
                if (loaded && video.autoplay) void video.play().catch(() => {})
              } else {
                video.pause()
              }
            },
            { rootMargin },
          )
        : null

    const prepare = () => {
      video.hidden = !mediaQuery.matches
      if (!mediaQuery.matches) {
        observer?.unobserve(video)
        video.pause()
        return
      }
      if (observer) observer.observe(video)
      else load()
    }

    if (document.readyState === 'complete') prepare()
    else window.addEventListener('load', prepare, { once: true })
    mediaQuery.addEventListener('change', prepare)

    return () => {
      observer?.disconnect()
      window.removeEventListener('load', prepare)
      mediaQuery.removeEventListener('change', prepare)
    }
  }
}
